import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Badge,
  Pagination,
  Form,
  Alert,
} from "react-bootstrap";
import { FaDownload, FaFileInvoice, FaCalendarAlt } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserInvoices } from "../../redux/slices/invoiceSlice";
import Spinner from "../common/Spinner";
import { generateInvoicePDF } from "../../utils/pdfGenerator";
import { get } from "../../utils/makeRequest";

const MyInvoicesTab = () => {
  const dispatch = useDispatch();
  const {
    invoices = [],
    loading,
    error,
  } = useSelector((state) => state.invoices);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [pdfLoading, setPdfLoading] = useState(null); // Track which invoice is being processed

  useEffect(() => {
    dispatch(fetchUserInvoices());
  }, [dispatch]);

  // Pagination logic
  const totalPages = Math.ceil((invoices?.length || 0) / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentInvoices =
    invoices?.slice(indexOfFirstItem, indexOfLastItem) || [];

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleDownload = async (invoice) => {
    try {
      setPdfLoading(invoice.id);

      const response = await get(`/payment/invoices/${invoice.id}`);
      const detailedInvoice = response;

      await generateInvoicePDF(detailedInvoice);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download invoice. Please try again.");
    } finally {
      setPdfLoading(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      paid: "success",
      pending: "warning",
      overdue: "danger",
    };
    return (
      <Badge bg={variants[status] || "secondary"} className="px-2 py-1">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="my-invoices-tab p-3">
      {error && <Alert variant="danger">{error}</Alert>}

      <div className="d-flex justify-content-center">
        <Card style={{ width: "100%" }} className="shadow-sm">
          <Card.Header className="bg-primary text-white py-3">
            <div className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0 fw-bold">
                <FaFileInvoice className="me-2" />
                My Invoices
              </h4>
              <div className="text-end">
                <Badge bg="light" text="dark" className="px-2 py-1">
                  {invoices?.length || 0} Total Invoices
                </Badge>
              </div>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table className="mb-0" hover>
                <thead className="table-light">
                  <tr>
                    <th className="px-4 py-3">Invoice ID</th>
                    <th className="px-4 py-3">Description</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {!currentInvoices?.length ? (
                    <tr>
                      <td colSpan="6" className="text-center py-5">
                        <div className="text-muted">
                          <FaFileInvoice
                            size={48}
                            className="mb-3 opacity-50"
                          />
                          <p className="mb-1">No invoices found.</p>
                          <small>
                            Your invoices will appear here once generated.
                          </small>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentInvoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td className="px-4 py-3">
                          <div className="fw-bold text-primary">
                            #{invoice.invoice_number}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-dark">{invoice.plan_name}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="fw-bold text-success">
                            {invoice.currency === "INR"
                              ? `₹ ${parseFloat(invoice.total_amount).toFixed(
                                  2
                                )}`
                              : `$${parseFloat(invoice.total_amount).toFixed(
                                  2
                                )}`}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-dark">
                            <FaCalendarAlt
                              className="me-1 text-muted"
                              size={12}
                            />
                            {formatDate(invoice.invoice_date)}
                          </div>
                          <small className="text-muted">
                            {formatTime(invoice.invoice_date)}
                          </small>
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(invoice.status)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => handleDownload(invoice)}
                              disabled={pdfLoading === invoice.id}
                            >
                              {pdfLoading === invoice.id ? (
                                <div
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                >
                                  <span className="visually-hidden">
                                    Loading...
                                  </span>
                                </div>
                              ) : (
                                <FaDownload />
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>

          {/* Pagination - keeping existing code */}
          {(invoices?.length || 0) > itemsPerPage && (
            <Card.Footer className="bg-light">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <small className="text-muted me-3">
                    Showing {indexOfFirstItem + 1} to{" "}
                    {Math.min(indexOfLastItem, invoices?.length || 0)} of{" "}
                    {invoices?.length || 0} invoices
                  </small>
                  <div className="d-flex align-items-center">
                    <small className="text-muted me-2">Show:</small>
                    <Form.Select
                      size="sm"
                      style={{ width: "auto" }}
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={15}>15</option>
                      <option value={20}>20</option>
                    </Form.Select>
                    <small className="text-muted ms-2">per page</small>
                  </div>
                </div>

                <Pagination className="mb-0" size="sm">
                  <Pagination.First
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                  />
                  <Pagination.Prev
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  />

                  {/* Page numbers */}
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    const isCurrentPage = pageNumber === currentPage;

                    if (
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 &&
                        pageNumber <= currentPage + 1)
                    ) {
                      return (
                        <Pagination.Item
                          key={pageNumber}
                          active={isCurrentPage}
                          onClick={() => handlePageChange(pageNumber)}
                        >
                          {pageNumber}
                        </Pagination.Item>
                      );
                    }

                    if (
                      (pageNumber === currentPage - 2 && currentPage > 3) ||
                      (pageNumber === currentPage + 2 &&
                        currentPage < totalPages - 2)
                    ) {
                      return <Pagination.Ellipsis key={pageNumber} disabled />;
                    }

                    return null;
                  })}

                  <Pagination.Next
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  />
                  <Pagination.Last
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              </div>
            </Card.Footer>
          )}
        </Card>
      </div>
    </div>
  );
};

export default MyInvoicesTab;
